import { useEffect, useRef, useState } from 'react';

import { useGetNoteQuery } from '../store/api';

/** Maximum time (ms) polling can stay active before auto-disabling */
const POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

interface UseNoteWithPollingOptions {
  /** Skip the query entirely */
  skip?: boolean;
  /** Polling interval in ms (default: 5000) */
  pollingInterval?: number;
}

/**
 * Custom hook that fetches note data and auto-polls when sections are loading
 * or extras are processing.
 *
 * Automatically enables polling when:
 * - Any section has empty text (still loading)
 * - Any extra has status 'processing'
 *
 * @param noteId - The note ID to fetch
 * @param options - Query options
 * @returns RTK Query result with auto-polling
 *
 * @example
 * ```typescript
 * const { data: noteData, isLoading, isError } = useNoteWithPolling(noteId, { skip: !noteId });
 * ```
 */
export function useNoteWithPolling(
  noteId: string | undefined,
  options: UseNoteWithPollingOptions = {},
) {
  const [shouldPoll, setShouldPoll] = useState(false);
  const pollingStartRef = useRef<number | null>(null);

  const queryResult = useGetNoteQuery(
    { noteId: noteId ?? '' },
    {
      skip: options.skip || !noteId,
      pollingInterval: shouldPoll ? (options.pollingInterval ?? 5000) : 0,
    },
  );

  // Auto-enable/disable polling based on note content state
  useEffect(() => {
    if (!queryResult.data?.content?.sections) {
      setShouldPoll(false);
      return;
    }

    // Check if any sections have empty text (still loading)
    const hasEmptySections = queryResult.data.content.sections.some(
      (section: any) => section.text === '',
    );

    // Check if any extras are still processing
    const hasProcessingExtras =
      queryResult.data.content.extras?.some((extra: any) => extra.status === 'processing') ||
      false;

    const wantsPolling = hasEmptySections || hasProcessingExtras;

    if (wantsPolling) {
      // Record when polling started; stop if it's been too long
      if (!pollingStartRef.current) {
        pollingStartRef.current = Date.now();
      } else if (Date.now() - pollingStartRef.current > POLLING_TIMEOUT_MS) {
        console.warn(
          `[useNoteWithPolling] Polling timed out after ${POLLING_TIMEOUT_MS / 1000}s for note ${noteId}`,
        );
        setShouldPoll(false);
        return;
      }
    } else {
      // Reset the timer when polling conditions clear
      pollingStartRef.current = null;
    }

    setShouldPoll(wantsPolling);
  }, [queryResult.data, noteId]);

  // Reset timeout when noteId changes (new note)
  useEffect(() => {
    pollingStartRef.current = null;
  }, [noteId]);

  return queryResult;
}
